/**
 * Um pedido do `request-flow-api` legado (código `REQ-…`) esperando o aprovador na fila de
 * Aprovações (#4416). Não é voucher: nunca vira reserva. Dois tipos — saldo numa carteira de
 * despesa (`type BALANCE`, `service PAYMENT` ou vazio) e acesso à mobilidade por um período
 * (`service MOBILITY`, sem valor).
 *
 * Datas do período são o DIA (YYYY-MM-DD) em UTC, como o `request-flow-api` grava;
 * `requestedAt` é o instante ISO-8601 UTC do pedido.
 */
import { z } from 'zod';

/** O `modal` que o `request-flow-api` publica na decisão (`Modal.RequestFlow`). */
export const APPROVAL_MODAL_REQUEST_FLOW = 'REQUEST_FLOW' as const;

export const RequestFlowKindSchema = z.enum(['wallet_balance', 'mobility_access']);
export type RequestFlowKind = z.infer<typeof RequestFlowKindSchema>;

/** `service` do legado → tipo do pedido: só `MOBILITY` é acesso; o resto é saldo. */
export function requestFlowKindFromService(service: string | null | undefined): RequestFlowKind {
  return service === 'MOBILITY' ? 'mobility_access' : 'wallet_balance';
}

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const PendingRequestFlowItemSchema = z.object({
  walletId: z.string().min(1),
  walletName: z.string().nullable(),
  days: z.number().int().nonnegative().nullable(),
  dailyRate: z.number().nonnegative().nullable(),
  /** `null` quando a origem não diz o valor ou a moeda (#6877): o pedido segue na fila, o item
   *  vai sem valor e o `totals` do pedido sai vazio. */
  amount: z.number().nonnegative().nullable(),
  currency: z.string().min(1).nullable(),
});

export const PendingRequestFlowSchema = z.object({
  requestFlowId: z.string().min(1),
  code: z.string().min(1),
  kind: RequestFlowKindSchema,
  requestedAt: z.iso.datetime(),
  period: z
    .object({ startAt: isoDay, endAt: isoDay, days: z.number().int().positive() })
    .nullable(),
  description: z.string().nullable(),
  motive: z.object({ name: z.string(), observation: z.string().optional() }).nullable(),
  costCenterName: z.string().nullable(),
  items: z.array(PendingRequestFlowItemSchema),
  totals: z.array(z.object({ amount: z.number().nonnegative(), currency: z.string().min(1) })),
});
export type PendingRequestFlow = z.infer<typeof PendingRequestFlowSchema>;
